import type { Permission } from '@mm/shared';
import {
  BarChart3,
  CalendarCheck,
  Coins,
  LayoutDashboard,
  type LucideIcon,
  Megaphone,
  Receipt,
  Settings,
  Smartphone,
  Sunrise,
  Target,
  Wallet,
} from 'lucide-react';

export interface NavItem {
  label: string;
  to?: string;
  icon?: LucideIcon;
  permission?: Permission;
  /** Implementation phase in which the page arrives (shown as a hint until then). */
  phase?: number;
  children?: NavItem[];
}

/** Sidebar structure (specification §52). */
export const NAVIGATION: NavItem[] = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard },
  {
    label: 'Morning Data Entry',
    to: '/morning-entry',
    icon: Sunrise,
    permission: 'transactions.write',
  },
  { label: 'Apps', to: '/apps', icon: Smartphone },
  { label: 'Marketing', to: '/marketing', icon: Megaphone },
  { label: 'Revenue', to: '/revenue', icon: Wallet },
  { label: 'Month-End Actuals', to: '/month-end', icon: CalendarCheck },
  { label: 'Expenses', to: '/expenses', icon: Receipt },
  {
    label: 'Reports',
    icon: BarChart3,
    children: [
      { label: 'Daily P&L', to: '/reports/daily' },
      { label: 'Monthly P&L', to: '/reports/monthly' },
      { label: 'Yearly P&L', to: '/reports/yearly' },
      { label: 'App Performance', to: '/reports/apps' },
      { label: 'Marketing Report', to: '/reports/marketing' },
      { label: 'Revenue Report', to: '/reports/revenue' },
      { label: 'GST Report', to: '/reports/gst' },
    ],
  },
  { label: 'Currency Rates', to: '/currency-rates', icon: Coins },
  {
    label: 'Accounts',
    icon: Target,
    children: [
      { label: 'Ads Accounts', to: '/ads-accounts' },
      { label: 'MCC Accounts', to: '/mcc-accounts' },
      { label: 'AdMob Accounts', to: '/admob-accounts' },
      { label: 'AdX Accounts', to: '/adx-accounts' },
      { label: 'GST Accounts', to: '/gst-accounts' },
      { label: 'Play Developer Accounts', to: '/play-developer-accounts' },
      { label: 'Firebase Accounts', to: '/firebase-accounts' },
    ],
  },
  { label: 'Settings', to: '/settings', icon: Settings },
];

/** Remove items (and empty groups) the current role may not use. */
export function visibleNavigation(items: NavItem[], can: (p: Permission) => boolean): NavItem[] {
  return items.flatMap((item) => {
    if (item.permission && !can(item.permission)) return [];
    if (item.children) {
      const children = visibleNavigation(item.children, can);
      return children.length ? [{ ...item, children }] : [];
    }
    return [item];
  });
}
